'use client';

import { Button } from '@/components/ui/buttons/Button';
import { useTranslation } from '@/features/i18n';
import { Link } from '@/ui/Link';
import { Input } from '@/ui/form/Input';
import { Label } from '@/ui/form/Label';
import { ApplicationLogo } from '@/ui/images/ApplicationLogo';
import { logger } from '@/utils/loggerUtils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuthentication } from '@vimond/features/authentication';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

export const ResetPasswordForm = () => {
  const { resetPassword } = useAuthentication();

  const { t } = useTranslation();

  const [errorMessage, setErrorMessage] = useState<string>();

  const FormSchema = z.object({
    email: z.string().email(t('auth.validate.invalid-email-address')),
  });

  type FormSchemaType = z.infer<typeof FormSchema>;
  const {
    formState: { errors, isDirty, isLoading, isSubmitting },
    handleSubmit,
    register,
  } = useForm<FormSchemaType>({
    resolver: zodResolver(FormSchema),
    values: {
      email: '',
    },
  });

  const onSubmit: SubmitHandler<FormSchemaType> = async (data) => {
    const { email } = data;
    await resetPassword(email)
      .then((res) => {
        if (res.isPasswordReset) {
          setErrorMessage(undefined);
          // redirect to login page
          window.location.href = '/auth/signin';
        } else {
          window.location.href = '/auth/password/confirm';
        }
      })
      .catch((err) => {
        setErrorMessage(t('auth.validate.invalid-email-address'));
        logger.error(err);
      });
  };

  return (
    <section>
      <div className="mx-auto flex flex-col items-center justify-center px-6 py-8 lg:h-screen lg:py-0">
        <ApplicationLogo />
        <div className="border-background-border bg-background-secondary w-full rounded-lg border shadow sm:max-w-md md:mt-0 xl:p-0">
          <div className="space-y-4 p-6 sm:p-8 md:space-y-6">
            <h1 className="text-center text-xl font-bold leading-tight tracking-tight md:text-2xl">
              {t('auth.reset-password')}
            </h1>

            <form
              className="space-y-4 md:space-y-6"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div>
                <Label htmlFor="email">{t('auth.email-address')}</Label>
                <div className="mt-2">
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    {...register('email')}
                    placeholder={t('auth.email-placeholder')}
                    aria-describedby="email-error"
                  />
                </div>
                <p
                  id="email-error"
                  className="text-red-500"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {errors.email?.message}
                </p>
              </div>

              <div>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={isLoading || isSubmitting || !isDirty}
                  type="submit"
                  className="w-full"
                >
                  {t('auth.reset-password')}
                </Button>
                {errorMessage && <p className="text-red-500">{errorMessage}</p>}
              </div>
            </form>

            <p className="text-text-secondary mt-10 text-center text-sm">
              {t('auth.dont-have-an-account')}{' '}
              <Link
                href="/auth/signup"
                className="text-text-highlight font-semibold leading-6"
              >
                {t('auth.sign-up-now')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
